import { Box, TextField, Typography } from '@mui/material';
import { SegmentEffects } from '@opengov/app-core/analytics';
import { DuplicateItemsDialog, Folder, ItemsNames } from '@opengov/bnp-component-folders/src';
import { getTextFieldEndAdornment } from '@opengov/capital-mui-theme';
import { ValidationStates } from '@opengov/component-library/dist';
import { IFolder, IReportV4 } from '@opengov/reporting-types';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { useToastContext } from '../../../../../src/components/Toast/contexts/useToast.context';
import { foldersAndReportsQueryKeys } from '../../../../../src/pages/reportsPage/hooks/useGetFoldersAndReports/foldersAndReports.queryKeys';
import { useSelectedFoldersAndReports } from '../../../../../src/pages/reportsPage/hooks/useSelectedFoldersAndReports/useSelectedFoldersAndReports';
import {
  buildActionErrorToastBody,
  buildActionSuccessToastBody,
  getFoldersAndReportsPhrase,
  PageEvents
} from '../../../../../src/pages/reportsPage/ReportsPage.utils';
import { FoldersAndReportsActionErrorsDialog } from '../../../../../src/pages/reportsPage/components/FoldersAndReportsActionErrorsDialog/FoldersAndReportsActionErrorsDialog';
import { ACTION_ERROR_CODES } from '../../../../../src/pages/reportsPage/components/FoldersAndReportsActionErrorsDialog/FoldersAndReportsActionErrorsDialog.utils';
import {
  DuplicateFoldersAndReportsPayload,
  DuplicateItemsResponse,
  useDuplicateFoldersAndReports
} from './hooks/useDuplicateFoldersAndReports/useDuplicateFoldersAndReports';
import { computeDuplicateReportTitle } from './utils/computeDuplicateReportTitle';
import { REPORT_DESCRIPTION_MAX_LENGTH, REPORT_TITLE_MAX_LENGTH } from '../../../forms/reportForm/types';

const intl = {
  title: 'Duplicate',
  itemsNamesAccordionTitle: 'Show',
  reportTitle: 'Report Title',
  reportDescription: 'Report Description',
  toast: {
    success: {
      title: `Duplication completed`,
      body: buildActionSuccessToastBody('duplicated', 'duplicate')
    },
    error: {
      title: `Failed to duplicate`,
      body: buildActionErrorToastBody('duplicate')
    }
  },
  actionErrorsDialog: {
    title: 'Some Errors Occurred While Duplicating',
    unexpectedError: 'could not be duplicated due to an unexpected error. Please retry.',
    codeToPhrase: {
      [ACTION_ERROR_CODES.NOT_FOUND_OR_PERMISSION_DENIED]:
        'could not be duplicated due to restricted permissions or absence.',
      [ACTION_ERROR_CODES.FOLDERS_NESTING_EXCEEDS_7_LEVELS]:
        'could not be duplicated due to folders nesting exceeding 7 levels.'
    }
  }
};

export type DuplicateFoldersAndReportsCustomDuplicateParams = {
  parentFolderId: string;
  reportTitle: string;
  reportDescription: string;
};

type DuplicateFoldersAndReportsDialogProps = {
  selectedItems: (IFolder | IReportV4)[];
  invalidateFoldersQuery?: (parentFolderId?: Folder['id']) => Promise<void>;
  onSelectedItemRemove?: (itemId: string) => void;
  onClose: () => void;
  /**
   * Optional override for the title field's initial value in single-report
   * mode. When omitted, the dialog defaults to
   * {@link computeDuplicateReportTitle} applied to the first selected
   * report's title (e.g. `"My Report [COPY]"`). Pass an explicit value when
   * the source title needs to be derived from something other than the
   * selected report (e.g. a standard template's name in the report-viewer
   * Duplicate flow).
   */
  initialTitle?: string;
  /**
   * When provided, renders a status end-adornment (success check / error
   * indicator) on the title input. Used by the report-viewer Duplicate flow
   * which validates the title via the legacy report-form state machine.
   */
  titleValidationState?: ValidationStates;
  /**
   * Fires on every keystroke in the title input, in addition to the dialog's
   * own internal state. Used to keep an external state machine in sync.
   */
  onTitleChange?: (title: string) => void;
  /** Fires on title input blur. */
  onTitleBlur?: (title: string) => void;
  /**
   * Fires on every keystroke in the description input, in addition to the
   * dialog's own internal state.
   */
  onDescriptionChange?: (description: string) => void;
  /** Fires on description input blur. */
  onDescriptionBlur?: (description: string) => void;
  /**
   * Custom duplicate handler for callers that need a different downstream
   * effect than the default v4 bulk-duplicate endpoint (e.g. the report
   * viewer's create-custom-report-and-redirect flow).
   *
   * When provided, the dialog awaits this function instead of calling
   * {@link useDuplicateFoldersAndReports}, skips the default success toast
   * and react-query invalidations, and calls {@link onClose} on success.
   * The caller is responsible for any post-duplicate side effects
   * (navigation, toasts, query invalidation, etc).
   *
   * Only honored in single-report mode.
   */
  onCustomDuplicate?: (params: DuplicateFoldersAndReportsCustomDuplicateParams) => Promise<void>;
};

export function DuplicateFoldersAndReportsDialog({
  selectedItems,
  invalidateFoldersQuery,
  onSelectedItemRemove,
  onClose,
  initialTitle,
  titleValidationState,
  onTitleChange,
  onTitleBlur,
  onDescriptionChange,
  onDescriptionBlur,
  onCustomDuplicate
}: Readonly<DuplicateFoldersAndReportsDialogProps>) {
  const { items, selectedFolderIds, selectedReportIds, selectedFolders, selectedReports } =
    useSelectedFoldersAndReports(selectedItems);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const { mutateAsync: duplicateFoldersAndReports } = useDuplicateFoldersAndReports();
  const { toggleToast } = useToastContext();
  const queryClient = useQueryClient();
  const currentFolderId = `${selectedItems[0].parentFolderId ?? 'none'}`;
  const [failedDuplicatedResponse, setFailedDuplicatedResponse] = useState<
    DuplicateItemsResponse['failed'] & { parentFolderId: string }
  >({
    folders: [],
    reports: [],
    total: 0,
    parentFolderId: ''
  });
  const foldersAndReportsPhrase = getFoldersAndReportsPhrase({
    foldersCount: selectedFolderIds.length,
    reportsCount: selectedReportIds.length
  });

  const isSingleReport = selectedFolderIds.length === 0 && selectedReportIds.length === 1;
  const defaultSingleReportTitle = isSingleReport ? computeDuplicateReportTitle(selectedReports[0]?.title ?? '') : '';
  const [reportTitle, setReportTitle] = useState(initialTitle ?? defaultSingleReportTitle);
  const [reportDescription, setReportDescription] = useState('');
  const maxTitleLength = REPORT_TITLE_MAX_LENGTH;
  const maxDescriptionLength = REPORT_DESCRIPTION_MAX_LENGTH;

  const handleReportTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportTitle(event.target.value);
    onTitleChange?.(event.target.value);
  };

  const handleReportTitleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onTitleBlur?.(event.target.value);
  };

  const handleReportDescriptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setReportDescription(event.target.value);
    onDescriptionChange?.(event.target.value);
  };

  const handleReportDescriptionBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onDescriptionBlur?.(event.target.value);
  };

  const trackDuplicateEvent = () => {
    void SegmentEffects.track({
      eventName: PageEvents.DuplicateReports,
      props: { reportsCount: selectedReportIds.length }
    });
  };

  const handleCustomDuplicate = async (destinationFolderId: string) => {
    if (!onCustomDuplicate) {
      return;
    }
    trackDuplicateEvent();
    try {
      setIsDuplicating(true);
      await onCustomDuplicate({
        parentFolderId: destinationFolderId,
        reportTitle,
        reportDescription
      });
      onClose();
    } catch {
      // The custom-duplicate caller is responsible for surfacing the failure
      // (its own toast / navigation). We just reset the loading state and
      // leave the dialog open so the user can retry.
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleDuplicate = async (payload: DuplicateFoldersAndReportsPayload) => {
    trackDuplicateEvent();

    try {
      setIsDuplicating(true);
      const { duplicated, failed } = await duplicateFoldersAndReports(payload);

      try {
        if (duplicated.reports.length > 0 || duplicated.folders.length > 0) {
          await queryClient.invalidateQueries({ queryKey: foldersAndReportsQueryKeys.all });
        }
        if (duplicated.folders.length > 0 && invalidateFoldersQuery) {
          await invalidateFoldersQuery();
        }
      } finally {
        setIsDuplicating(false);
        if (duplicated.total > 0 && failed.total === 0) {
          setFailedDuplicatedResponse({ folders: [], reports: [], total: 0, parentFolderId: '' });
          onClose();
          toggleToast({
            severity: 'success',
            title: intl.toast.success.title,
            body: intl.toast.success.body(
              duplicated.reports.length,
              failed.reports.length,
              duplicated.folders.length,
              failed.folders.length
            )
          });
        } else if (failed.total > 0) {
          setFailedDuplicatedResponse({ ...failed, parentFolderId: payload.destinationFolderId });
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : intl.toast.error.body(selectedReportIds.length, selectedFolderIds.length);
      toggleToast({
        severity: 'error',
        title: intl.toast.error.title,
        body: errorMessage
      });
      setIsDuplicating(false);
      onClose();
    }
  };

  if (failedDuplicatedResponse.total > 0) {
    return (
      <FoldersAndReportsActionErrorsDialog
        {...intl.actionErrorsDialog}
        folders={selectedFolders}
        reports={selectedReports}
        failedActionResponse={failedDuplicatedResponse}
        isRetrying={isDuplicating}
        onClose={onClose}
        onRetry={handleDuplicate}
      />
    );
  }
  const titleEndAdornment =
    titleValidationState !== undefined && titleValidationState !== ValidationStates.Error
      ? (getTextFieldEndAdornment('success') as React.ReactNode)
      : undefined;

  return (
    <DuplicateItemsDialog
      open
      title={`${intl.title} ${foldersAndReportsPhrase}`}
      onDuplicate={(parentFolderId) => {
        const destinationFolderId = `${parentFolderId}`;
        if (onCustomDuplicate && isSingleReport) {
          void handleCustomDuplicate(destinationFolderId);
          return;
        }
        void handleDuplicate({
          sourceFolderIds: selectedFolderIds,
          sourceReportIds: selectedReportIds,
          destinationFolderId,
          ...(isSingleReport && { reportDescription, reportTitle })
        });
      }}
      onClose={onClose}
      isDuplicating={isDuplicating}
      currentFolderId={currentFolderId}
      data-test="duplicateReportsDialog"
    >
      {isSingleReport ? (
        <Box sx={{ '& .cds-textFieldCounter': { paddingBottom: 0 } }}>
          <Box sx={{ paddingBottom: '24px' }}>
            <TextField
              id="duplicateReportTitle"
              data-test="duplicateReportTitle"
              fullWidth
              inputProps={{ maxLength: maxTitleLength }}
              InputProps={{ endAdornment: titleEndAdornment }}
              value={reportTitle}
              onChange={handleReportTitleChange}
              onBlur={handleReportTitleBlur}
              label={
                <Box className="cds-textFieldLabelWithCounter">
                  <Typography variant="h4">{intl.reportTitle}</Typography>
                  <Typography variant="bodySmall" className="cds-textFieldCounter">
                    {reportTitle.length}/{maxTitleLength}
                  </Typography>
                </Box>
              }
              variant="outlined"
            />
          </Box>
          <Box>
            <TextField
              id="reportDescriptionInput"
              data-testid="reportDescriptionInput"
              data-test="reportDescriptionInput"
              multiline
              rows={4}
              fullWidth
              inputProps={{ maxLength: maxDescriptionLength }}
              value={reportDescription}
              onChange={handleReportDescriptionChange}
              onBlur={handleReportDescriptionBlur}
              label={
                <Box className="cds-textFieldLabelWithCounter">
                  <Typography variant="h4">{intl.reportDescription}</Typography>
                  <Typography variant="bodySmall" className="cds-textFieldCounter">
                    {reportDescription.length}/{maxDescriptionLength}
                  </Typography>
                </Box>
              }
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={{ '& .MuiInputBase-multiline': { width: '100%', cursor: 'text' } }}
            />
          </Box>
        </Box>
      ) : (
        <ItemsNames
          accordionTitle={`${intl.itemsNamesAccordionTitle} ${foldersAndReportsPhrase}`}
          items={items}
          onDelete={(item) => {
            if (onSelectedItemRemove && item.id !== null) {
              onSelectedItemRemove(item.id);
            }
          }}
        />
      )}
    </DuplicateItemsDialog>
  );
}
